import {FormGroup} from "@angular/forms";
import {QuickFormGroup} from "../helpers/quick-form-group";
import {ContactModel, contactModelFactory} from "../contact/contact.model";
import gql from "graphql-tag";
import {GqlModel, GqlModelOperator, GqlMutationModel, GqlQueryModel} from "../graphql/graphql.model";
import {
  AvailableStandaloneAccessoryModel,
  StandaloneAccessoryGqlModel,
  StandaloneAccessoryInputDataGqlModel,
} from "../accessory/standalone-accessory.gql-model";
import {JSONModel, jsonProperty} from "../json.model";
import {AddressModel, addressModelFactory} from "../address/address.model";

export class AssetBundledAccessoryModel extends JSONModel {
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") serialNumber: string;
  @jsonProperty("string") productDescription: string;
  @jsonProperty("boolean") changed: boolean;
}

export class AssetAvailableBulkOrderItemModel extends JSONModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("string") orderDate: string;
  @jsonProperty("string") quantity: string;
  @jsonProperty("string") quantityAvailable: string;
}

export class AssetAvailableProductModel extends JSONModel {
  @jsonProperty("string") productId: string;
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") modelName: string;
  @jsonProperty("string") familyName: string;
  @jsonProperty("string") manufacturer: string;
}

export class AssetDispositionModel extends JSONModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") dispositionText: string;
}

export class AssetAvailableDeviceModel extends JSONModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") deviceKey: string;
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") productDescription: string;
  @jsonProperty([AssetAvailableBulkOrderItemModel]) availableBulkOrderItems: AssetAvailableBulkOrderItemModel[];
  @jsonProperty([AssetAvailableProductModel]) availableProducts: AssetAvailableProductModel[];
  @jsonProperty([AssetBundledAccessoryModel]) bundledAccessories: AssetBundledAccessoryModel[];
  @jsonProperty(AssetDispositionModel) disposition: AssetDispositionModel;
}

export class AssetGqlModel extends GqlQueryModel {
  static mapper: GqlModelOperator<AssetGqlModel> = GqlModel.buildMapper(AssetGqlModel, "asset");

  static query: any = gql`
    fragment contactDetails on Contact {
      firstName
      lastName
      phoneNumber
      phoneNumber2
      emailAddress
      locale
    }

    fragment bulkOrderDetails on BulkOrderLineItem {
      id
      orderNumber
      orderDate
      quantity
      quantityAvailable
    }

    fragment productDetails on K2Product {
      productId
      familyName
      productNumber
      modelName
    }

    fragment dispositionDetails on Disposition {
      dispositionText
      isFutureFleet
      id
    }

    fragment bundledAccessoryDetails on BundledAccessory {
      productDescription
      productNumber
    }
    
    fragment addressDetails on Address {
      name1
      name2
      line1
      line2
      city
      region
      postalCode
      country
    }

    query getAsset($assetId: ID!) {
      asset: asset(assetId: $assetId) {
        id
        siteId
        deviceId
        serialNumber
        installDate
        updateDate
        originalInstallDate
        assetTag
        itsmConsumableAutoFulfillment
        itsmLongLifeConsumableAutoFulfillment
        itsmMaintenanceAutoFulfillment
        building
        category
        travelZone
        deliveryTypeModifier
        deliveryLocationModifier
        partsLogisticsSuppliesModifier
        coverageWindowModifier
        responseTimeModifier
        repairTimeModifier
        expressExchange
        depotRepair
        supportType
        healthCheck
        floor
        floorMapId
        department
        ipAddress
        gateway
        subnet
        hostName
        macAddress
        grid
        installDate
        assetServiceAddressId
        serviceAddress {
          ...addressDetails
        }
        assetTonerAddressId
        tonerAddress {
          ...addressDetails
        }
        hpInstalledKit
        disposition {
          ...dispositionDetails
        }
        dispositionId
        deviceKey
        productId
        productNumber
        modelName
        manufacturer
        orderDate
        orderNumber
        bulkOrderAssetId
        eotLeaseDate
        sourceName
        caseNumber
        replacementProductNumber
        replacementProductSerial
        customHeading1
        customHeading2
        customHeading3
        customHeading4
        customHeading5
        custom1
        custom2
        custom3
        custom4
        custom5
        custom6
        custom7
        custom8
        custom9
        custom10
        preconfigServiceNumber
        dispositions {
          ...dispositionDetails
        }
        availableProducts {
          ...productDetails
        }
        standaloneAccessories {
          id
          assetId
          siteId
          deviceKey
          deviceId
          productNumber
          productDescription
          serialNumber
          building
          floor
          orderNumber
          orderDate
          bulkOrderAccessoryId
          availableBulkOrderItems {
            ...bulkOrderDetails
          }
        }
        availableStandaloneAccessories {
          id
          deviceKey
          productNumber
          productDescription
          disabled
          availableBulkOrderItems {
            ...bulkOrderDetails
          }
        }
        bundledAccessories {
          ...bundledAccessoryDetails
          serialNumber
        }
        availableBulkOrderItems {
          ...bulkOrderDetails
        }
        tonerContact {
          ...contactDetails
        }
        installationContact {
          ...contactDetails
        }
        breakfixContact {
          ...contactDetails
        }
        availableDevices {
          id
          deviceKey
          productNumber
          productDescription
          disposition {
            ...dispositionDetails
          }
          availableBulkOrderItems {
            ...bulkOrderDetails
          }
          availableProducts {
            ...productDetails
          }
          bundledAccessories {
            ...bundledAccessoryDetails
          }
        }
      }
    }
  `;

  @jsonProperty("string") id: string;
  @jsonProperty("string") siteId: string;
  @jsonProperty("string") deviceId: string;
  @jsonProperty("string") serialNumber: string;
  @jsonProperty("string") assetTag: string;
  @jsonProperty("string") building: string;
  @jsonProperty("string") floor: string;
  @jsonProperty("string") floorMapId: string;
  @jsonProperty("string") department: string;
  @jsonProperty("string") ipAddress: string;
  @jsonProperty("stringDate") originalInstallDate: string;
  @jsonProperty("string") gateway: string;
  @jsonProperty("string") subnet: string;
  @jsonProperty("string") hostName: string;
  @jsonProperty("string") macAddress: string;
  @jsonProperty("stringDate") installDate: string;
  @jsonProperty("stringDate") updateDate: string;
  @jsonProperty("string") grid: string;
  @jsonProperty("boolean", "disposition.isFutureFleet") isFutureFleet: boolean;
  @jsonProperty("string") dispositionId: string;
  @jsonProperty("string", "disposition.dispositionText") dispositionText: string;
  @jsonProperty("string") deviceKey: string;
  @jsonProperty("string") productId: string;
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") modelName: string;
  @jsonProperty("string") manufacturer: string;
  @jsonProperty("string") orderDate: string;
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("string") bulkOrderAssetId: string;
  @jsonProperty("boolean", "bulkOrderAssetId") useBulkOrdering: boolean;
  @jsonProperty("string") eotLeaseDate: string;
  @jsonProperty("string") sourceName: string;
  @jsonProperty("string") caseNumber: string;
  @jsonProperty("string") replacementProductNumber: string;
  @jsonProperty("string") replacementProductSerial: string;
  @jsonProperty("string") customHeading1: string;
  @jsonProperty("string") customHeading2: string;
  @jsonProperty("string") customHeading3: string;
  @jsonProperty("string") customHeading4: string;
  @jsonProperty("string") customHeading5: string;
  @jsonProperty("string") custom1: string;
  @jsonProperty("string") custom2: string;
  @jsonProperty("string") custom3: string;
  @jsonProperty("string") custom4: string;
  @jsonProperty("string") custom5: string;
  @jsonProperty("string") custom6: string;
  @jsonProperty("string") custom7: string;
  @jsonProperty("string") custom8: string;
  @jsonProperty("string") custom9: string;
  @jsonProperty("string") custom10: string;
  @jsonProperty("string") geoRegionId: string;
  @jsonProperty("string") category: string;
  @jsonProperty("string") travelZone: string;
  @jsonProperty("string") deliveryTypeModifier: string;
  @jsonProperty("string") deliveryLocationModifier: string;
  @jsonProperty("string") partsLogisticsSuppliesModifier: string;
  @jsonProperty("string") coverageWindowModifier: string;
  @jsonProperty("string") responseTimeModifier: string;
  @jsonProperty("string") repairTimeModifier: string;
  @jsonProperty("string") expressExchange: string;
  @jsonProperty("string") depotRepair: string;
  @jsonProperty("string") supportType: string;
  @jsonProperty("string") healthCheck: string;
  @jsonProperty("string") preconfigServiceNumber: string;
  @jsonProperty("boolean") hpInstalledKit: boolean;
  @jsonProperty("boolean") itsmConsumableAutoFulfillment: boolean;
  @jsonProperty("boolean") itsmLongLifeConsumableAutoFulfillment: boolean;
  @jsonProperty("boolean") itsmMaintenanceAutoFulfillment: boolean;
  @jsonProperty([StandaloneAccessoryGqlModel]) standaloneAccessories: StandaloneAccessoryGqlModel[];
  @jsonProperty([AssetBundledAccessoryModel]) bundledAccessories: AssetBundledAccessoryModel[];
  @jsonProperty([AssetAvailableBulkOrderItemModel]) availableBulkOrderItems: AssetAvailableBulkOrderItemModel[];
  @jsonProperty([AssetAvailableProductModel]) availableProducts: AssetAvailableProductModel[];

  @jsonProperty(contactModelFactory) tonerContact: ContactModel;
  @jsonProperty(contactModelFactory) installationContact: ContactModel;
  @jsonProperty(contactModelFactory) breakfixContact: ContactModel;

  @jsonProperty(addressModelFactory) serviceAddress: AddressModel;
  @jsonProperty(addressModelFactory) tonerAddress: AddressModel;
  @jsonProperty([AssetDispositionModel]) dispositions: AssetDispositionModel[];

  @jsonProperty([AssetAvailableDeviceModel])
  availableDevices: AssetAvailableDeviceModel[];

  @jsonProperty([AvailableStandaloneAccessoryModel])
  availableStandaloneAccessories: AvailableStandaloneAccessoryModel[];

  formGroup: FormGroup;

  static variables(assetId: string) {
    return {assetId};
  }

  constructor(data: any) {
    super(data);

    for (const accessory of this.standaloneAccessories) {
      accessory.original = true;
      accessory.keep = true;
      accessory.changed = false;
    }

    for (const accessory of this.bundledAccessories) {
      accessory.changed = false;
    }

    this.formGroup = new QuickFormGroup(this, [
      "id",
      "deviceId",
      "serialNumber",
      "assetTag",
      "building",
      "floor",
      "itsmConsumableAutoFulfillment",
      "itsmLongLifeConsumableAutoFulfillment",
      "itsmMaintenanceAutoFulfillment",
      "floorMapId",
      "department",
      "ipAddress",
      "gateway",
      "subnet",
      "hostName",
      "category",
      "travelZone",
      "deliveryTypeModifier",
      "deliveryLocationModifier",
      "partsLogisticsSuppliesModifier",
      "coverageWindowModifier",
      "responseTimeModifier",
      "repairTimeModifier",
      "expressExchange",
      "depotRepair",
      "supportType",
      "healthCheck",
      "macAddress",
      "grid",
      "dispositionId",
      "deviceKey",
      "originalInstallDate",
      "updateDate",
      "productId",
      "productNumber",
      "modelName",
      "manufacturer",
      "orderDate",
      "orderNumber",
      "bulkOrderAssetId",
      "useBulkOrdering",
      "eotLeaseDate",
      "sourceName",
      "caseNumber",
      "replacementProductNumber",
      "replacementProductSerial",
      "installDate",
      "hpInstalledKit",
      "custom1",
      "custom2",
      "custom3",
      "custom4",
      "custom5",
      "custom6",
      "custom7",
      "custom8",
      "custom9",
      "custom10",
      "preconfigServiceNumber",
    ]);
  }
}

export class AssetInputResponseGqlModel extends GqlModel {
  @jsonProperty("string") id: string;
  @jsonProperty("boolean") updated: boolean;
}

export class AssetInputGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<AssetInputResponseGqlModel> =
    GqlModel.buildMapper(AssetInputResponseGqlModel, "asset");

  static allKeys: (keyof (AssetInputGqlModel & AssetGqlModel))[] = [
    "serialNumber",
    "assetTag",
    "building",
    "floor",
    "floorMapId",
    "department",
    "ipAddress",
    "gateway",
    "subnet",
    "hostName",
    "macAddress",
    "grid",
    "installDate",
    "hpInstalledKit",
    "itsmConsumableAutoFulfillment",
    "itsmLongLifeConsumableAutoFulfillment",
    "itsmMaintenanceAutoFulfillment",
    "category",
    "travelZone",
    "deliveryTypeModifier",
    "deliveryLocationModifier",
    "partsLogisticsSuppliesModifier",
    "coverageWindowModifier",
    "responseTimeModifier",
    "repairTimeModifier",
    "expressExchange",
    "depotRepair",
    "supportType",
    "healthCheck",
    "preconfigServiceNumber",
  ];

  static futureKeys: (keyof AssetGqlModel)[] = [
    "deviceId",
    "orderDate",
    "orderNumber",
    "productId",
    "eotLeaseDate",
    "sourceName",
    "bulkOrderAssetId",
    "custom1",
    "custom2",
    "custom3",
    "custom4",
    "custom5",
    "custom6",
    "custom7",
    "custom8",
    "custom9",
    "custom10",
  ];

  static removalKeys: (keyof AssetGqlModel)[] = [
    "productNumber",
    "modelName",
    "manufacturer",
    "dispositionId",
    "caseNumber",
    "replacementProductNumber",
    "replacementProductSerial",
  ];

  static query = gql`
    mutation updateAsset($assetId: ID!, $asset: AssetInput!) {
      asset: asset(assetId: $assetId, asset: $asset) {
        id
        updated
      }
    }
  `;

  @jsonProperty("string") assetId: string;
  @jsonProperty(AssetInputGqlModel.makeAssetModel) asset: {[key: string]: any};

  static makeAssetModel(asset: AssetGqlModel) {
    const assetData: any = {};

    for (const key of AssetInputGqlModel.allKeys) {
      if (asset.formGroup.controls[key].dirty) {
        assetData[key] = asset[key];
      }
    }

    if (asset.isFutureFleet) {
      for (const key of AssetInputGqlModel.futureKeys) {
        if (asset.formGroup.controls[key].dirty) {
          assetData[key] = asset[key];
        }
      }

      if (asset.standaloneAccessories.length) {
        const change = [];
        const remove = [];

        for (const accessory of asset.standaloneAccessories) {
          if (!accessory.keep && accessory.id) {
            remove.push(accessory.id);
          }

          if (accessory.changed) {
            change.push(new StandaloneAccessoryInputDataGqlModel(accessory));
          }
        }

        if (change.length) assetData.changeStandaloneAccessories = change;
        if (remove.length) assetData.removeStandaloneAccessories = remove;
        if (asset.preconfigServiceNumber !== asset.formGroup.value.preconfigServiceNumber) {
          assetData.preconfigServiceNumber = asset.preconfigServiceNumber;
        }
      }

      if (asset.bundledAccessories.length) {
        const changes = asset.bundledAccessories
          .filter(it => it.changed && it.productNumber)
          .map(it => ({productNumber: it.productNumber, serialNumber: it.serialNumber || null}));

        if (changes.length) assetData.changedBundledAccessorySerialNumbers = changes;
      }
    } else {
      for (const key of AssetInputGqlModel.removalKeys) {
        if (asset.formGroup.controls[key].dirty) {
          assetData[key] = asset[key];
        }
      }
    }

    return assetData;
  }
}
